
import React, { useRef, useState } from 'react';
import styles from './ImageUpload.module.css';

interface ImageUploadProps {
    onImageSelect: (file: File | null) => void;
    previewUrl?: string; // URL de previsualización inicial (opcional)
    className?: string;
    isLoading?: boolean;
}

export const ImageUpload: React.FC<ImageUploadProps> = ({
    onImageSelect,
    previewUrl,
    className,
    isLoading = false
}) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const cameraInputRef = useRef<HTMLInputElement>(null);
    const [localPreview, setLocalPreview] = useState<string | null>(previewUrl || null);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            processFile(file);
        }
    };

    const processFile = (file: File) => {
        // Validar tipo
        if (!file.type.startsWith('image/')) {
            // Podríamos mostrar un error aquí si tuviéramos un sistema de toast integrado en el componente
            alert('Por favor selecciona un archivo de imagen válido');
            return;
        }

        // Crear preview local
        const url = URL.createObjectURL(file);
        setLocalPreview(url);

        // Notificar al padre
        onImageSelect(file);
    };

    const handleRemove = (e: React.MouseEvent) => {
        e.stopPropagation(); // Evitar abrir el selector de archivos
        setLocalPreview(null);
        onImageSelect(null); // Notificar eliminación

        // Limpiar inputs
        if (fileInputRef.current) fileInputRef.current.value = '';
        if (cameraInputRef.current) cameraInputRef.current.value = '';
    };

    const triggerSelect = () => {
        fileInputRef.current?.click();
    };

    const triggerCamera = (e: React.MouseEvent) => {
        e.stopPropagation();
        cameraInputRef.current?.click();
    };

    return (
        <div className={`${styles.container} ${className || ''}`}>
            <div
                className={`${styles.uploadArea} ${isLoading ? styles.disabled : ''}`}
                onClick={!localPreview && !isLoading ? triggerSelect : undefined}
            >
                {isLoading && (
                    <div className={styles.loadingOverlay}>
                        <div className={styles.spinner}></div>
                    </div>
                )}

                {localPreview ? (
                    <div className={styles.previewContainer}>
                        <img src={localPreview} alt="Vista previa" className={styles.previewImage} />
                        {!isLoading && (
                            <button
                                type="button"
                                className={styles.removeButton}
                                onClick={handleRemove}
                                title="Eliminar imagen"
                            >
                                ✕
                            </button>
                        )}
                    </div>
                ) : (
                    <div className={styles.placeholder}>
                        <div className={styles.icon}>📷</div>
                        <span className={styles.label}>
                            Haz clic para subir una imagen
                        </span>
                        <span style={{ fontSize: '0.9rem', color: '#94a3b8' }}>
                            o arrastra y suelta aquí
                        </span>

                        {/* Botón visible solo en móvil/tablet para cámara explícita si se desea */}
                        <div className={styles.buttonsContainer}>
                            <button
                                type="button"
                                className={styles.mobileButton}
                                onClick={triggerCamera}
                            >
                                📸 Tomar Foto
                            </button>
                        </div>
                    </div>
                )}

                {/* Input estándar para archivos */}
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className={styles.hiddenInput}
                    onChange={handleFileChange}
                    disabled={isLoading}
                />

                {/* Input específico para cámara (capture="environment") */}
                <input
                    ref={cameraInputRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    className={styles.hiddenInput}
                    onChange={handleFileChange}
                    disabled={isLoading}
                />
            </div>
        </div>
    );
};
